import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Modal, View } from 'react-native';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import FormActions from '../../../components/FormActions';
import { translate } from '../../../services/translate';
import { WindowInformation } from '../../../services/window-information';
import RadioList from '../../../components/ModernFilters/RadioList';

export type UnitRole = 'driver' | 'responsible';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    role: UnitRole;
    unit: AppAgentUnit;
    onConfirm: (userId: number) => Promise<boolean>;
}

interface FormValues {
    userId: number | null;
}

export default function ChangeUnitRoleModal({ isModalVisible, setModalVisible, role, unit, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const form = useForm<FormValues>({ defaultValues: { userId: null } });
    const userId = useWatch({ control: form.control, name: 'userId' });

    const currentUserId = useMemo(() => {
        const current = unit.UnitUsers.find(uu => role === 'driver' ? uu.UnitUser.isDriver : uu.UnitUser.isPersonInCharge);
        return current?.id ?? null;
    }, [unit, role]);

    const items = useMemo(() => {
        return unit.UnitUsers
            .filter(uu => uu.active)
            .map(uu => ({
                label: uu.warName || uu.name,
                value: uu.id,
            }));
    }, [unit]);

    useEffect(() => {
        if (isModalVisible) {
            form.reset({ userId: currentUserId });
        }
    }, [isModalVisible, currentUserId]);

    function closeModal() {
        setModalVisible(false);
    }

    async function handleSubmit() {
        if (userId === null) {
            return;
        }

        try {
            setIsLoading(true);
            const success = await onConfirm(userId);
            if (success) {
                closeModal();
            }
        } finally {
            setIsLoading(false);
        }
    }

    const title = role === 'driver' ? translate('changeDriver') : translate('changePersonInCharge');
    const subtitle = role === 'driver' ? translate('changeDriverDescription') : translate('changePersonInChargeDescription');

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={closeModal}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{title}</MyAppText>
                        <MyAppText style={styles.subheader}>{subtitle}</MyAppText>
                    </View>

                    <FormProvider {...form}>
                        <View style={styles.listContainer}>
                            <RadioList<FormValues, 'userId', number | null>
                                name='userId'
                                items={items}
                                clearValue={null}
                                hideClearValue
                                disabled={isLoading}
                            />
                        </View>
                    </FormProvider>

                    <FormActions
                        viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        onSubmit={handleSubmit}
                        onClose={closeModal}
                        disabled={userId === null || userId === currentUserId || isLoading}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 24,
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    listContainer: {
        maxHeight: 360,
    },
}));
